import { ButtonCancel } from "@/components/common/Button";
import { FormSingleColumn } from "@/components/specific/pipeline/FormSingleColumn";
import { Webhook, WebhookEvent } from "@/types/domain";
import { FormHeader } from "@/components/specific/pipeline/FormHeader";
import { Label } from "@/components/common/FormLabel";
import { Input } from "@/components/common/Input";
import { LoadingPage } from "@/components/common/Loading";
import { useEffect, useState } from "react";
import { getWebhook, getWebhookEvents } from "@/api/webhook";
import { Alert } from "@/components/common/Alert/Alert";
import { useNavigate, useParams } from "react-router";
import { Button } from "@/components/shadcn/button";
import { ClipboardCopy, Eye, EyeOff } from "lucide-react";
import { useClipboard } from "@/hooks/useClipboard";
import dayjs from "dayjs";
import { WebhookEventTable } from "@/components/specific/webhook/WebhookEventTable";
import { ConfirmWebhookSecretGenerationDialog } from "@/components/specific/webhook/ConfirmWebhookSecretGenerationDialog";
import { WebhookGuidanceAccordion } from "@/components/specific/webhook/WebhookGuidanceAccordion";

export const WebhookMaintenanceView = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [webhook, setWebhook] = useState<Webhook | null>(null);
  const [alert, setAlert] = useState<string | null>(null);
  const [loading, setIsLoading] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [webhookEvents, setWebhookEvents] = useState<WebhookEvent[]>([]);
  const { copyToClipboard } = useClipboard();

  const fetchWebhookEvents = async () => {
    try {
      setIsLoading(true);
      setWebhookEvents(await getWebhookEvents());
    } catch (err) {
      setAlert("Failed to load webhook events, please reload the page.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWebhookEvents();
  }, []);

  const groupedEvents = webhookEvents.reduce<Record<string, WebhookEvent[]>>(
    (group, event) => {
      const category = event.categoryName || "Uncategorised";
      if (!group[category]) {
        group[category] = [];
      }
      group[category].push(event);
      return group;
    },
    {}
  ); //Always provide initial value to reduce when using TypeScript with. This resolves the type inference issue.

  const fetchWebhook = async (id: string): Promise<Webhook> => {
    const webhook = (await getWebhook(id)) as Webhook;
    setWebhook(webhook);
    return webhook;
  };

  useEffect(() => {
    if (id) {
      fetchWebhook(id);
    }
  }, [id]);

  return (
    <>
      <FormSingleColumn onSubmit={() => {}}>
        <FormHeader
          title="Webhook"
          formInformation="Viewing an existing webhook. This is where you can see information about a webhook. Click Edit to update and configure its options."
        >
          <Button
            type="button"
            variant="success"
            size={"sm"}
            className="bg-kPurple-500 hover:bg-kPurple-700 text-white font-bold"
            onClick={() => {navigate(`/maintenance/webhook/edit/${id}`)}}
          >
            Edit Webhook
          </Button>
          <ButtonCancel text="Back To List" className="text-kPurple-500" />
        </FormHeader>
        <div className="grid gap-4 gap-y-2 text-sm grid-cols-1 md:grid-cols-5">
          {alert && (
            <div className="md:col-span-5">
              <Alert variant="error" persistent={true}>
                {alert}
              </Alert>
            </div>
          )}
          <div className="md:col-span-5">
            <Label forField="name" label={"Name"} />
            <p className="font-normal text-gray-600 py-1">
              The name of the webhook</p>
            <div className="md:w-[86ch]">
              <Input
                readOnly={true}
                value={webhook?.name || ""} />
            </div>
          </div>
          <div className="md:col-span-5">
            <Label forField="url" label={"Url"} />
            <p className="font-normal text-gray-600 py-1">
              The url where the webhook will send its payloads.
            </p>
            <div className="md:w-[86ch]">
              <Input readOnly={true} value={webhook?.url || ""} className="" />
            </div>
          </div>
          <div className="md:col-span-5">
            <Label forField="description" label={"Description"} />
            <p className="font-normal text-gray-600 py-1">
              The description for your webhook</p>
            <div className="md:w-[86ch]">
              <Input
                readOnly={true}
                multiline={true}
                rows={5}
                value={webhook?.description || ""} />
            </div>
          </div>
          <div className="md:col-span-5 mt-1">
            <Label forField="secret" label={"Secret"} />
            <p className="font-normal text-gray-600 py-1">
              The secret used to secure and encode the webhook payloads.
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1 md:w-[64ch] md:flex-none">
                <Input
                  readOnly={true}
                  type={showSecret ? "text" : "password"}
                  value={webhook?.secret || ""}
                />
              </div>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setShowSecret((prev) => !prev)}
                title={showSecret ? "Hide secret" : "Show secret"}
              >
                {showSecret ? <EyeOff size={18}  /> : <Eye size={18} />}
              </button>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                onClick={() => copyToClipboard(webhook?.secret || "")}
                title={"Copy secret to clipboard"}
              >
                <ClipboardCopy size={18} />
              </button>
              <ConfirmWebhookSecretGenerationDialog
                id={webhook?.id}
                dialogOpenState={false}
                callBack={async () => {
                  await fetchWebhook(id || "");
                }}
                onClose={() => {}}
              />
            </div>
            <div className="basis-full mt-2 flex gap-2">
              <Label forField="" label={"Secret Last Updated:"} />
              <span className="text-gray-600">
                {dayjs(webhook?.secretGeneratedDate).format("YYYY-MM-DD HH:mm:ss")}
              </span>
            </div>
          </div>
          <div className="md:col-span-5 mt-2">
            <Label forField="isActive" label={"Status"} />
            <p className="font-normal text-gray-600 py-1">
              Webhooks which are inactive will not receive any notifications.
            </p>
            {webhook?.isActive ? (
              <span title="Click 'Edit Webhook' button to set inactive" className="inline-flex items-center gap-1 text-green-600 font-semibold">
                Webhook is currently Active
              </span>
            ) : (
              <span title="Click 'Edit Webhook' button to set active" className="inline-flex items-center gap-1 text-red-600 font-semibold">
                Webhook is currently Inactive
              </span>
            )}
          </div>

          <div className="md:col-span-5 mt-4">
            <div className="text-gray-600">
              <p className="font-semibold text-lg">Events</p>
              <p className="font-normal text-gray-600 py-1">
                Which events this webhook is currently subscribed to and will be notified of?
              </p>
              <hr className="py-2" />
            </div>
          </div>
          <div className="md:col-span-5">
            {loading ? (
              <LoadingPage />
            ) : (
              <WebhookEventTable groupedEvents={groupedEvents} webhook={webhook} />
            )}
          </div>
          <div className="md:col-span-5 mt-8">
            <div className="text-gray-600">
              <p className="font-semibold text-lg">Information</p>
              <p className="font-normal text-gray-600 py-1">
                General information about this webhook.
              </p>
              <hr className="py-2" />
            </div>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3">
            <div className="">
              <Label forField="" label={"Created By"} />
              <span className="block mt-2 text-gray-600">
                {webhook?.createdByName} - {webhook?.createdDate
                  ? dayjs(webhook.createdDate).format("YYYY-MM-DD HH:mm:ss") : ""}
              </span>
            </div>
            <div className="">
              <Label forField="" label={"Last Updated By"} />
              <span className="block mt-2 text-gray-600">
                {webhook?.updatedByName} - {webhook?.updatedDate
                  ? dayjs(webhook.updatedDate).format("YYYY-MM-DD HH:mm:ss") : ""}
              </span>
            </div>
          </div>
          <WebhookGuidanceAccordion />
        </div>
      </FormSingleColumn>
    </>
  );
};
